import { useEffect, useRef, useState } from "react";

// A sidebar that is either pinned open or folded to an icon rail. The choice
// is kept in localStorage under `storageKey`; `fallback` decides when nothing
// is stored yet. While folded, resting the pointer on it (or tabbing into it)
// opens it over the page for a moment — `peek`. Spread `handlers` on the panel.
export function usePinnedSidebar(storageKey, fallback = () => true) {
  const [pinned, setPinned] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved !== null) return saved === "1";
    } catch {
      /* storage unavailable: use the fallback */
    }
    return fallback();
  });
  const [peek, setPeek] = useState(false);
  const timer = useRef(null);

  // A short wait before opening and closing, so a pointer passing over the
  // rail does not flash the panel open.
  const later = (fn, ms) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(fn, ms);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const togglePin = () => {
    const next = !pinned;
    clearTimeout(timer.current);
    setPinned(next);
    setPeek(false);
    try {
      window.localStorage.setItem(storageKey, next ? "1" : "0");
    } catch {
      /* storage unavailable: the choice lasts for this visit only */
    }
  };

  const handlers = {
    onMouseEnter: () => !pinned && later(() => setPeek(true), 140),
    onMouseLeave: () => later(() => setPeek(false), 180),
    onFocus: (e) => !pinned && e.target.matches?.(":focus-visible") && setPeek(true),
    onBlur: (e) => !e.currentTarget.contains(e.relatedTarget) && setPeek(false),
  };

  return { pinned, peek, open: pinned || peek, togglePin, closePeek: () => setPeek(false), handlers };
}
